import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Eye, EyeOff, Mail, Lock, User, Building, Globe, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import SEO from '../SEO';
import RateLimitNotice from '../ui/RateLimitNotice';
import LandingNav from '../Layout/LandingNav';
import LandingFooter from '../Layout/LandingFooter';
import GoogleButton from './GoogleButton';
import { PLANS } from '../../config/plans';
import { track, EVENTS } from '../../utils/analytics';

const VALID_PLANS = new Set(PLANS.map((p) => p.slug));
const VALID_BILLING = new Set(['monthly', 'annual']);

const startCheckoutFlow = async (planParam, billingParam) => {
  const plan = String(planParam || '').toLowerCase();
  const billing = String(billingParam || 'monthly').toLowerCase();
  if (!VALID_PLANS.has(plan) || !VALID_BILLING.has(billing)) return false;

  const token = localStorage.getItem('accessToken');
  if (!token) return false;

  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/lemon-squeezy/checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'ngrok-skip-browser-warning': 'true',
      },
      body: JSON.stringify({ priceId: `${plan}_${billing}` }),
    });
    const data = await response.json();
    if (data?.success && data?.data?.checkoutUrl) {
      window.location.href = data.data.checkoutUrl;
      return true;
    }
  } catch (e) {
    // fall through to normal post-register navigation
  }
  return false;
};

function RegisterPage() {
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showRateLimit, setShowRateLimit] = useState(false);
  const { register: registerUser, googleLogin, isLoading, error, clearError } = useAuth();
  const { theme } = useTheme();
  const d = theme === 'dark';
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get('plan');
  const billingParam = searchParams.get('billing') || 'monthly';

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setError,
    trigger,
  } = useForm();

  const password = watch('password');

  const onSubmit = async (data) => {
    try {
      clearError();
      setShowRateLimit(false);
      // Fired on submit, not on page view: this is the first point at which the
      // visitor has actually committed to creating an account.
      track(EVENTS.SIGNUP_STARTED, { plan: planParam || 'none', billing: billingParam });
      await registerUser({
        email: data.email,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
        company: data.company,
        websiteUrl: data.websiteUrl,
      });
      track(EVENTS.SIGNUP_COMPLETED, { plan: planParam || 'none', billing: billingParam });
      toast.success('Account created successfully!');
      if (planParam) {
        const ok = await startCheckoutFlow(planParam, billingParam);
        if (ok) return;
      }
    } catch (err) {
      // Reason only — never the submitted email or any other field value.
      track(EVENTS.SIGNUP_FAILED, {
        reason: err.isRateLimit || err.status === 429 ? 'rate_limited' : 'error',
      });
      if (err.isRateLimit || err.status === 429 || err.message.includes('Too many')) {
        setShowRateLimit(true);
        return;
      }

      if (err.message.includes('email')) {
        setError('email', { message: err.message });
      } else if (err.message.includes('password')) {
        setError('password', { message: err.message });
      } else {
        toast.error(err.message || 'Registration failed');
      }
    }
  };

  const inputClass = (hasError) => `
    block w-full pl-10 pr-3 py-3 rounded-[12px] border text-sm transition-colors
    ${hasError
      ? d ? 'border-red-500/40 focus:border-red-500' : 'border-red-300 focus:border-red-500'
      : d ? 'border-white/[0.08] focus:border-blue-500/50' : 'border-gray-200 focus:border-blue-400'
    }
    ${d ? 'bg-white/[0.03] text-white placeholder-gray-600' : 'bg-gray-100 text-gray-900 placeholder-gray-400'}
    focus:outline-none focus:ring-1 ${hasError ? 'focus:ring-red-500/30' : 'focus:ring-blue-500/30'}
  `;

  return (
    <div className={`min-h-screen flex flex-col transition-colors duration-300 ${d ? 'bg-[#0a0a0a]' : 'bg-white'}`}>
      <SEO
        title="Sign up"
        description="Create your PaperBrainz account."
        path="/register"
        noindex
      />
      <LandingNav />

      <div className="flex-1 flex items-center justify-center px-4 pt-44 pb-32">
        <div className="w-full max-w-md space-y-5">
          {/* Card */}
          <div className="">
            {/* Header */}
            <div className="text-center mb-10">
              <h2 className={`text-3xl md:text-4xl font-semibold tracking-tight ${d ? 'text-white' : 'text-gray-900'}`}>
                {step === 1 ? 'Create your account' : 'Set your password'}
              </h2>
              <p className={`mt-2 text-sm ${d ? 'text-gray-500' : 'text-gray-500'}`}>
                {step === 1 ? 'Get started with PaperBrainz today' : 'Almost there — secure your account'}
              </p>
              {/* Step indicator */}
              <div className="flex items-center justify-center gap-2 mt-5">
                <div className={`h-1 w-8 rounded-full transition-colors ${d ? 'bg-white' : 'bg-black'}`} />
                <div className={`h-1 w-8 rounded-full transition-colors ${step === 2 ? (d ? 'bg-white' : 'bg-black') : (d ? 'bg-white/10' : 'bg-gray-200')}`} />
              </div>
            </div>

            {/* Google Sign-Up (only on step 1) */}
            {step === 1 && (
              <>
                <GoogleButton d={d} text="Sign up with Google" onSuccess={async (credential) => {
                  try {
                    clearError();
                    await googleLogin(credential);
                    toast.success('Account created!');
                    if (planParam) {
                      await startCheckoutFlow(planParam, billingParam);
                    }
                  } catch (err) {
                    toast.error(err.message || 'Google sign-up failed');
                  }
                }} />

                <div className="flex items-center gap-3 mb-6">
                  <div className={`flex-1 h-px ${d ? 'bg-white/[0.06]' : 'bg-gray-200'}`} />
                  <span className={`text-xs ${d ? 'text-gray-600' : 'text-gray-400'}`}>or</span>
                  <div className={`flex-1 h-px ${d ? 'bg-white/[0.06]' : 'bg-gray-200'}`} />
                </div>
              </>
            )}

            {/* Form */}
            <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
              {showRateLimit && (
                <RateLimitNotice onClose={() => setShowRateLimit(false)} />
              )}

              {error && !showRateLimit && (
                <div className={`rounded-[12px] border p-3.5 ${d ? 'bg-red-500/[0.08] border-red-500/20' : 'bg-red-50 border-red-200'}`}>
                  <p className={`text-sm ${d ? 'text-red-400' : 'text-red-600'}`}>{error}</p>
                </div>
              )}

              {step === 1 && (
                <>
                  {/* Name Fields */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${d ? 'text-gray-300' : 'text-gray-700'}`}>
                        First Name
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                          <User className={`h-4.5 w-4.5 ${d ? 'text-gray-600' : 'text-gray-400'}`} />
                        </div>
                        <input
                          {...register('firstName', {
                            required: 'First name is required',
                            minLength: { value: 2, message: 'Min 2 characters' },
                            maxLength: { value: 50, message: 'Max 50 characters' },
                          })}
                          type="text"
                          autoComplete="given-name"
                          className={inputClass(errors.firstName)}
                          placeholder="First name"
                        />
                      </div>
                      {errors.firstName && (
                        <p className={`mt-1.5 text-xs ${d ? 'text-red-400' : 'text-red-500'}`}>{errors.firstName.message}</p>
                      )}
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-2 ${d ? 'text-gray-300' : 'text-gray-700'}`}>
                        Last Name
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                          <User className={`h-4.5 w-4.5 ${d ? 'text-gray-600' : 'text-gray-400'}`} />
                        </div>
                        <input
                          {...register('lastName', {
                            required: 'Last name is required',
                            minLength: { value: 2, message: 'Min 2 characters' },
                            maxLength: { value: 50, message: 'Max 50 characters' },
                          })}
                          type="text"
                          autoComplete="family-name"
                          className={inputClass(errors.lastName)}
                          placeholder="Last name"
                        />
                      </div>
                      {errors.lastName && (
                        <p className={`mt-1.5 text-xs ${d ? 'text-red-400' : 'text-red-500'}`}>{errors.lastName.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${d ? 'text-gray-300' : 'text-gray-700'}`}>
                      Email Address
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                        <Mail className={`h-4.5 w-4.5 ${d ? 'text-gray-600' : 'text-gray-400'}`} />
                      </div>
                      <input
                        {...register('email', {
                          required: 'Email is required',
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'Please enter a valid email address',
                          },
                        })}
                        type="email"
                        autoComplete="email"
                        className={inputClass(errors.email)}
                        placeholder="you@example.com"
                      />
                    </div>
                    {errors.email && (
                      <p className={`mt-1.5 text-xs ${d ? 'text-red-400' : 'text-red-500'}`}>{errors.email.message}</p>
                    )}
                  </div>

                  {/* Company */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${d ? 'text-gray-300' : 'text-gray-700'}`}>
                      Company <span className={d ? 'text-gray-600' : 'text-gray-400'}>(Optional)</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                        <Building className={`h-4.5 w-4.5 ${d ? 'text-gray-600' : 'text-gray-400'}`} />
                      </div>
                      <input
                        {...register('company', {
                          maxLength: { value: 100, message: 'Max 100 characters' },
                        })}
                        type="text"
                        autoComplete="organization"
                        className={inputClass(errors.company)}
                        placeholder="Your company name"
                      />
                    </div>
                    {errors.company && (
                      <p className={`mt-1.5 text-xs ${d ? 'text-red-400' : 'text-red-500'}`}>{errors.company.message}</p>
                    )}
                  </div>

                  {/* Website URL */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${d ? 'text-gray-300' : 'text-gray-700'}`}>
                      Website URL
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                        <Globe className={`h-4.5 w-4.5 ${d ? 'text-gray-600' : 'text-gray-400'}`} />
                      </div>
                      <input
                        {...register('websiteUrl', {
                          required: 'Website URL is required',
                          pattern: {
                            value: /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/,
                            message: 'Please enter a valid URL (e.g., https://example.com)',
                          },
                        })}
                        type="url"
                        autoComplete="url"
                        className={inputClass(errors.websiteUrl)}
                        placeholder="https://yourwebsite.com"
                      />
                    </div>
                    <p className={`mt-1.5 text-xs ${d ? 'text-gray-600' : 'text-gray-400'}`}>
                      Where will you install the chat widget?
                    </p>
                    {errors.websiteUrl && (
                      <p className={`mt-1.5 text-xs ${d ? 'text-red-400' : 'text-red-500'}`}>{errors.websiteUrl.message}</p>
                    )}
                  </div>

                  {/* Continue button */}
                  <button
                    type="button"
                    onClick={async () => {
                      const valid = await trigger(['firstName', 'lastName', 'email', 'company', 'websiteUrl']);
                      if (valid) setStep(2);
                    }}
                    className={`
                      w-full flex items-center justify-center py-3 px-4 rounded-[12px] text-sm font-medium transition-all duration-200
                      ${d ? 'bg-white text-black hover:bg-gray-200' : 'bg-black text-white hover:bg-gray-800'}
                    `}
                  >
                    Continue
                  </button>
                </>
              )}

              {step === 2 && (
                <>
                  {/* Password */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${d ? 'text-gray-300' : 'text-gray-700'}`}>
                      Password
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                        <Lock className={`h-4.5 w-4.5 ${d ? 'text-gray-600' : 'text-gray-400'}`} />
                      </div>
                      <input
                        {...register('password', {
                          required: 'Password is required',
                          minLength: { value: 8, message: 'Password must be at least 8 characters' },
                          pattern: {
                            value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&.#^()\-_=+\[\]{}|;:,<>\/\\])/,
                            message: 'Must contain uppercase, lowercase, number, and special character',
                          },
                        })}
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        className={`
                          block w-full pl-10 pr-12 py-3 rounded-[12px] border text-sm transition-colors
                          ${errors.password
                            ? d ? 'border-red-500/40 focus:border-red-500' : 'border-red-300 focus:border-red-500'
                            : d ? 'border-white/[0.08] focus:border-blue-500/50' : 'border-gray-200 focus:border-blue-400'
                          }
                          ${d ? 'bg-white/[0.03] text-white placeholder-gray-600' : 'bg-gray-100 text-gray-900 placeholder-gray-400'}
                          focus:outline-none focus:ring-1 ${errors.password ? 'focus:ring-red-500/30' : 'focus:ring-blue-500/30'}
                        `}
                        placeholder="Create a password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3.5 flex items-center"
                      >
                        {showPassword ? (
                          <EyeOff className={`h-4.5 w-4.5 ${d ? 'text-gray-600 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'}`} />
                        ) : (
                          <Eye className={`h-4.5 w-4.5 ${d ? 'text-gray-600 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'}`} />
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <p className={`mt-1.5 text-xs ${d ? 'text-red-400' : 'text-red-500'}`}>{errors.password.message}</p>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${d ? 'text-gray-300' : 'text-gray-700'}`}>
                      Confirm Password
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                        <Lock className={`h-4.5 w-4.5 ${d ? 'text-gray-600' : 'text-gray-400'}`} />
                      </div>
                      <input
                        {...register('confirmPassword', {
                          required: 'Please confirm your password',
                          validate: value => value === password || 'Passwords do not match',
                        })}
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        className={inputClass(errors.confirmPassword)}
                        placeholder="Confirm your password"
                      />
                    </div>
                    {errors.confirmPassword && (
                      <p className={`mt-1.5 text-xs ${d ? 'text-red-400' : 'text-red-500'}`}>{errors.confirmPassword.message}</p>
                    )}
                  </div>

                  {/* Back + Submit */}
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className={`flex items-center gap-1.5 px-4 py-3 rounded-[12px] text-sm font-medium border transition-colors ${
                        d ? 'border-white/[0.08] text-gray-300 hover:bg-white/[0.04]' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className={`
                        flex-1 flex items-center justify-center py-3 px-4 rounded-[12px] text-sm font-medium transition-all duration-200
                        ${isLoading
                          ? d ? 'bg-white/10 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : d ? 'bg-white text-black hover:bg-gray-200' : 'bg-black text-white hover:bg-gray-800'
                        }
                      `}
                    >
                      {isLoading ? (
                        <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      ) : (
                        'Create account'
                      )}
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>

          {/* Already have an account card */}
          <div className={`rounded-[16px] border p-5 flex items-center justify-between transition-colors duration-300 ${d ? 'bg-[#111] border-white/[0.06]' : 'bg-white border-gray-200 shadow-sm'}`}>
            <div>
              <p className={`text-sm font-medium ${d ? 'text-white' : 'text-gray-900'}`}>Already have an account?</p>
              <p className={`text-xs mt-0.5 ${d ? 'text-gray-500' : 'text-gray-500'}`}>Sign in to access your dashboard.</p>
            </div>
            <Link
              to="/login"
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                d ? 'border-white/[0.08] text-blue-400 hover:bg-white/[0.04]' : 'border-gray-200 text-blue-600 hover:bg-gray-50'
              }`}
            >
              Log in <span aria-hidden="true">&rarr;</span>
            </Link>
          </div>

          {/* Terms notice */}
          <p className={`text-center text-xs ${d ? 'text-gray-600' : 'text-gray-400'}`}>
            By confirming, you agree to our{' '}
            <Link to="/terms-of-service" className={`underline ${d ? 'hover:text-gray-400' : 'hover:text-gray-600'}`}>Terms of Service</Link>
            {' '}and{' '}
            <Link to="/privacy-policy" className={`underline ${d ? 'hover:text-gray-400' : 'hover:text-gray-600'}`}>Privacy Policy</Link>.
          </p>
        </div>
      </div>

      <LandingFooter />
    </div>
  );
}

export default RegisterPage;
